import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Alert, IconButton, Button,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const LEAVE_LABELS = {
  annual:         'Annual Leave',
  sick:           'Sick Leave',
  public_holiday: 'Public Holiday',
  other:          'Leave',
};

// Semantic cell colours — role is gone, only state matters
const C = {
  working:  '#10b981', // green  — in office
  off:      '#f59e0b', // amber  — full day off
  partial:  '#3b82f6', // blue   — partial absence
  weekend:  null,      // handled separately
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDaysInMonth(year, month) {
  const days = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    days.push(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function toDateStr(date) {
  return date.toISOString().split('T')[0];
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12  = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

function calcNetHours(startTime, endTime) {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60 - 0.5);
}

/**
 * Build a per-user, per-date leave lookup.
 *
 * approved[userId][dateStr] = { type, hours, isPartial }
 *   isPartial = single-day record with < 6 hours → blue cell
 *
 * pending[userId] = Set<dateStr>
 */
function buildLeaveMap(leaveRecords) {
  const approved = {};
  const pending  = {};

  for (const lr of leaveRecords) {
    const uid        = lr.user_id;
    const isApproved = lr.approved === 1;
    const isPending  = !lr.approved && !lr.rejected;
    if (!isApproved && !isPending) continue;

    const isSingleDay = lr.date_start === lr.date_end;
    const isPartial   = isSingleDay && (lr.hours ?? 8) < 6;

    const start = new Date(lr.date_start + 'T00:00:00');
    const end   = new Date(lr.date_end   + 'T00:00:00');
    const cur   = new Date(start);

    while (cur <= end) {
      const ds = toDateStr(cur);
      if (isApproved) {
        if (!approved[uid]) approved[uid] = {};
        approved[uid][ds] = { type: lr.type, hours: lr.hours ?? 8, isPartial };
      } else {
        if (!pending[uid]) pending[uid] = new Set();
        pending[uid].add(ds);
      }
      cur.setDate(cur.getDate() + 1);
    }
  }

  return { approved, pending };
}

// ─── DayCell ─────────────────────────────────────────────────────────────────

function DayCell({ date, user, isToday, isPast, isWeekend, leaveMap }) {
  const theme   = useTheme();
  const dateStr = toDateStr(date);

  // ── Weekend ──
  if (isWeekend) {
    return (
      <TableCell
        sx={{
          p: 0, width: 34, minWidth: 34, maxWidth: 34, height: 40,
          bgcolor: theme.palette.grey[100],
          borderLeft: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      />
    );
  }

  const dayKey      = DAY_KEYS[date.getDay()];
  const approvedInfo = leaveMap.approved[user.id]?.[dateStr];
  const isPending    = leaveMap.pending[user.id]?.has(dateStr);
  const dayData      = user.workHours?.[dayKey];
  const isWorking    = dayData?.type === 'working' && dayData.startTime && dayData.endTime;

  // ── Determine state ──
  let baseColor;
  let tip;

  if (approvedInfo) {
    if (approvedInfo.isPartial) {
      baseColor = C.partial;
      tip = `${LEAVE_LABELS[approvedInfo.type] || 'Leave'} — partial (${approvedInfo.hours} hrs)`;
    } else {
      baseColor = C.off;
      tip = LEAVE_LABELS[approvedInfo.type] || 'Leave';
    }
  } else if (isPending) {
    baseColor = C.off;   // pending treated as off, shown dashed
    tip = 'Leave requested — pending approval';
  } else if (isWorking) {
    baseColor = C.working;
    const net = calcNetHours(dayData.startTime, dayData.endTime);
    tip = `Working · ${formatTime(dayData.startTime)}–${formatTime(dayData.endTime)} · ${net % 1 === 0 ? net : net.toFixed(1)} hrs`;
  } else {
    baseColor = C.off;
    tip = 'Day off';
  }

  // Past days: wash out; future/today: full colour
  const fill = isPast ? alpha(baseColor, 0.3) : alpha(baseColor, 0.65);

  const todayBorderL = isToday ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.divider, 0.5)}`;
  const todayBorderR = isToday ? `2px solid ${theme.palette.primary.main}` : undefined;

  return (
    <Tooltip title={`${user.name} — ${tip}`} placement="top" arrow>
      <TableCell
        sx={{
          p: 0,
          width: 34, minWidth: 34, maxWidth: 34, height: 40,
          bgcolor: fill,
          borderLeft:   todayBorderL,
          borderRight:  todayBorderR,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          cursor: 'default',
          // Pending: dashed overlay
          ...(isPending && !approvedInfo && {
            bgcolor: alpha(baseColor, isPast ? 0.12 : 0.25),
            outline: `2px dashed ${alpha(baseColor, 0.6)}`,
            outlineOffset: '-3px',
          }),
        }}
      />
    </Tooltip>
  );
}

// ─── StaffScheduleSection ─────────────────────────────────────────────────────

export default function StaffScheduleSection() {
  const theme = useTheme();
  const { getToken, isAdmin } = useAuth();

  const today    = new Date();
  const todayStr = toDateStr(today);

  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const [users,    setUsers]    = useState([]);
  const [leaveMap, setLeaveMap] = useState({ approved: {}, pending: {} });
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const days            = getDaysInMonth(viewYear, viewMonth);
  const isCurrentMonth  = viewYear === today.getFullYear() && viewMonth === today.getMonth();

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goToPrev  = () => viewMonth === 0  ? (setViewYear(y => y - 1), setViewMonth(11)) : setViewMonth(m => m - 1);
  const goToNext  = () => viewMonth === 11 ? (setViewYear(y => y + 1), setViewMonth(0))  : setViewMonth(m => m + 1);
  const goToToday = () => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); };

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    const token = await getToken();
    const resp  = await fetch(`${config.apiUrl}/api/users?limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await resp.json();
    if (!data.success) return;

    const parsed = (data.data || [])
      .map(u => {
        let wh = null;
        try { wh = u.workHours ? (typeof u.workHours === 'string' ? JSON.parse(u.workHours) : u.workHours) : null; } catch { }
        return { ...u, workHours: wh };
      })
      .filter(u => u.workHours && Object.values(u.workHours).some(d => d?.type === 'working'))
      .sort((a, b) => {
        const order = ['admin', 'manager', 'sales', 'designer'];
        const ai = order.indexOf((a.role || '').toLowerCase());
        const bi = order.indexOf((b.role || '').toLowerCase());
        if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        return (a.name || '').localeCompare(b.name || '');
      });

    setUsers(parsed);
  }, [getToken]);

  const fetchLeave = useCallback(async () => {
    if (!isAdmin) return;
    const token = await getToken();
    const month = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
    const resp  = await fetch(`${config.apiUrl}/api/leave/all?month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await resp.json();
    if (data.success) setLeaveMap(buildLeaveMap(data.leave || []));
  }, [getToken, isAdmin, viewYear, viewMonth]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchUsers(), fetchLeave()]);
    } catch {
      setError('Failed to load staff schedules');
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, fetchLeave]);

  useEffect(() => { reload(); }, [reload]);

  // ── Summary footer counts ────────────────────────────────────────────────────

  const getDayStats = (date) => {
    if (date.getDay() === 0 || date.getDay() === 6) return { working: 0, off: 0, partial: 0 };
    const ds     = toDateStr(date);
    const dayKey = DAY_KEYS[date.getDay()];
    let working = 0, off = 0, partial = 0;
    for (const u of users) {
      const leave = leaveMap.approved[u.id]?.[ds];
      if (leave) {
        leave.isPartial ? partial++ : off++;
      } else if (u.workHours?.[dayKey]?.type === 'working') {
        working++;
      } else {
        off++;
      }
    }
    return { working, off, partial };
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        mt: 3,
      }}
    >
      <CardContent sx={{ p: 0 }}>

        {/* ── Header ── */}
        <Box
          sx={{
            px: 2.5, py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CalendarMonthIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>Staff Calendar</Typography>
              <Typography variant="caption" color="text.secondary">
                Monthly schedule — hover any day for details
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={goToPrev} aria-label="Previous month">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="subtitle1" fontWeight={700}
              sx={{ minWidth: 155, textAlign: 'center', userSelect: 'none' }}
            >
              {MONTH_NAMES[viewMonth]} {viewYear}
            </Typography>
            <IconButton size="small" onClick={goToNext} aria-label="Next month">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            {!isCurrentMonth && (
              <Button
                size="small" variant="outlined"
                startIcon={<TodayIcon sx={{ fontSize: 14 }} />}
                onClick={goToToday}
                sx={{ ml: 1, textTransform: 'none', py: 0.4, fontSize: '0.75rem' }}
              >
                Today
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={reload} disabled={loading} sx={{ ml: 0.5 }}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── States ── */}
        {error && <Alert severity="error" sx={{ m: 2.5 }}>{error}</Alert>}

        {loading && !users.length && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 7 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && !users.length && (
          <Box sx={{ textAlign: 'center', py: 7, px: 3 }}>
            <CalendarMonthIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="body1" fontWeight={500} color="text.secondary" gutterBottom>
              No schedules configured yet
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Set working hours for staff members in the Users page to see them here.
            </Typography>
          </Box>
        )}

        {/* ── Calendar grid ── */}
        {users.length > 0 && (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  minWidth: 160 + days.length * 34,
                }}
              >
                <colgroup>
                  <col style={{ width: 160 }} />
                  {days.map(d => <col key={d.getDate()} style={{ width: 34 }} />)}
                </colgroup>

                {/* ── Day-number header ── */}
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky', left: 0, zIndex: 3,
                        bgcolor: theme.palette.grey[50],
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        fontWeight: 700, fontSize: '0.68rem',
                        textTransform: 'uppercase', letterSpacing: 0.6,
                        color: 'text.secondary',
                        pl: 2.5, py: 1,
                        boxShadow: `2px 0 6px ${alpha('#000', 0.05)}`,
                      }}
                    >
                      Staff
                    </TableCell>

                    {days.map(date => {
                      const dow       = date.getDay();
                      const isWeekend = dow === 0 || dow === 6;
                      const isToday   = toDateStr(date) === todayStr;
                      const isPast    = date < today && !isToday;

                      return (
                        <TableCell
                          key={date.getDate()}
                          align="center"
                          sx={{
                            p: 0,
                            width: 34, minWidth: 34, maxWidth: 34,
                            borderBottom: `2px solid ${isToday ? theme.palette.primary.main : theme.palette.divider}`,
                            borderLeft: isToday
                              ? `2px solid ${theme.palette.primary.main}`
                              : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                            borderRight: isToday ? `2px solid ${theme.palette.primary.main}` : undefined,
                            bgcolor: isWeekend
                              ? theme.palette.grey[100]
                              : isToday
                                ? alpha(theme.palette.primary.main, 0.07)
                                : theme.palette.grey[50],
                          }}
                        >
                          <Box sx={{ py: 0.9, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: isToday ? 800 : 600,
                                fontSize: '0.65rem', lineHeight: 1.2,
                                color: isToday
                                  ? theme.palette.primary.main
                                  : isWeekend
                                    ? 'text.disabled'
                                    : isPast
                                      ? alpha(theme.palette.text.secondary, 0.45)
                                      : 'text.secondary',
                              }}
                            >
                              {date.getDate()}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.52rem', lineHeight: 1.1,
                                textTransform: 'uppercase', letterSpacing: 0.2,
                                color: isToday ? theme.palette.primary.main : 'text.disabled',
                              }}
                            >
                              {DAY_ABBR[dow]}
                            </Typography>
                            {isToday && (
                              <Box sx={{ width: 4, height: 4, mt: 0.3, borderRadius: '50%', bgcolor: theme.palette.primary.main }} />
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                {/* ── Body ── */}
                <TableBody>
                  {users.map((user, idx) => {
                    const rowBg = idx % 2 === 1
                      ? alpha(theme.palette.primary.main, 0.012)
                      : theme.palette.background.paper;

                    return (
                      <TableRow key={user.id}>
                        {/* Name — sticky */}
                        <TableCell
                          sx={{
                            position: 'sticky', left: 0, zIndex: 2,
                            bgcolor: rowBg,
                            py: 0.75, pl: 2.5, pr: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            boxShadow: `2px 0 6px ${alpha('#000', 0.05)}`,
                            height: 40,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ mb: 0.3 }}>
                            {user.name}
                          </Typography>
                          <Chip
                            label={user.role}
                            size="small"
                            sx={{
                              height: 16, fontSize: '0.6rem', fontWeight: 700,
                              textTransform: 'capitalize',
                              bgcolor: alpha(theme.palette.text.secondary, 0.08),
                              color: 'text.secondary',
                              '& .MuiChip-label': { px: 0.7 },
                            }}
                          />
                        </TableCell>

                        {/* Day cells */}
                        {days.map(date => {
                          const isToday   = toDateStr(date) === todayStr;
                          const isPast    = date < today && !isToday;
                          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                          return (
                            <DayCell
                              key={date.getDate()}
                              date={date}
                              user={user}
                              isToday={isToday}
                              isPast={isPast}
                              isWeekend={isWeekend}
                              leaveMap={leaveMap}
                            />
                          );
                        })}
                      </TableRow>
                    );
                  })}

                  {/* ── Footer: daily stats ── */}
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky', left: 0, zIndex: 2,
                        bgcolor: theme.palette.grey[50],
                        py: 0.75, pl: 2.5,
                        borderTop: `2px solid ${theme.palette.divider}`,
                        borderBottom: 0,
                        fontWeight: 700, fontSize: '0.65rem',
                        textTransform: 'uppercase', letterSpacing: 0.4,
                        color: 'text.secondary',
                        boxShadow: `2px 0 6px ${alpha('#000', 0.05)}`,
                      }}
                    >
                      In Office
                    </TableCell>

                    {days.map(date => {
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                      const isToday   = toDateStr(date) === todayStr;
                      const { working, off, partial } = getDayStats(date);
                      const total     = users.length;
                      const allIn     = working === total && total > 0;

                      return (
                        <TableCell
                          key={date.getDate()}
                          align="center"
                          sx={{
                            p: '4px 2px',
                            width: 34, minWidth: 34, maxWidth: 34,
                            bgcolor: isWeekend
                              ? theme.palette.grey[100]
                              : isToday
                                ? alpha(theme.palette.primary.main, 0.06)
                                : theme.palette.grey[50],
                            borderTop: `2px solid ${isToday ? theme.palette.primary.main : theme.palette.divider}`,
                            borderLeft: isToday
                              ? `2px solid ${theme.palette.primary.main}`
                              : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                            borderRight: isToday ? `2px solid ${theme.palette.primary.main}` : undefined,
                            borderBottom: 0,
                          }}
                        >
                          {!isWeekend && (
                            <Tooltip
                              title={`${working} working${off > 0 ? ` · ${off} off` : ''}${partial > 0 ? ` · ${partial} partial` : ''}`}
                              placement="top"
                              arrow
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.6rem', fontWeight: 700, cursor: 'default',
                                  color: allIn
                                    ? C.working
                                    : working < total
                                      ? C.off
                                      : 'text.secondary',
                                }}
                              >
                                {working}/{total}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            {/* ── Legend ── */}
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 2.5,
                px: 2.5, py: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="caption" color="text.disabled"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}
              >
                Key
              </Typography>

              {[
                { color: C.working,  label: 'Working' },
                { color: C.off,      label: 'Day off / Leave' },
                { color: C.partial,  label: 'Partial leave' },
              ].map(({ color, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: alpha(color, 0.65), borderRadius: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>{label}</Typography>
                </Box>
              ))}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.grey[200], borderRadius: 0.5 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500}>Weekend</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <Box
                  sx={{
                    width: 16, height: 16,
                    bgcolor: alpha(C.off, 0.2),
                    border: `2px dashed ${alpha(C.off, 0.6)}`,
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption" color="text.secondary" fontWeight={500}>Pending leave</Typography>
              </Box>

              <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: '0.6rem' }}>
                Past days shown faded · Hover any cell for details
              </Typography>
            </Box>
          </>
        )}

      </CardContent>
    </Card>
  );
}
